import type { BiometricDataDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';
import { checkDuplicateCardEnrollment } from './use-cases/checkDuplicateCardEnrollmentUseCase';

/**
 * Gate input.
 *
 * Design note: The original design spec used `employee: Employee` (domain entity),
 * but the `Employee` domain entity does not expose raw biometric_data[].
 * The card duplicate check requires the raw BiometricDataDTO[] to inspect
 * decimal/decimal_short values. Using `biometricData: BiometricDataDTO[]` directly
 * keeps this gate pure and testable without requiring a domain entity change.
 * Phase 2 (AdminEnrollCardPage) must supply biometric_data from the EmployeeDTO
 * fetched via API, or from an extended employee object.
 */
export interface EnrollCardSaveGateInput {
  decimal: number;
  biometricData: BiometricDataDTO[];
}

export interface EnrollCardSaveGateResult {
  conflict: boolean;
  reason?: 'already-enrolled';
}

/**
 * Pure gate: runs local duplicate-card pre-check before persisting an enrollment.
 * Extracted to a dedicated module so it can be tested independently without
 * importing the AdminEnrollCardPage component (avoids react-refresh warnings).
 */
export function runEnrollCardSaveGate(
  input: EnrollCardSaveGateInput,
): EnrollCardSaveGateResult {
  const check = checkDuplicateCardEnrollment(input.biometricData, input.decimal);

  if (check.conflict) {
    return { conflict: true, reason: 'already-enrolled' };
  }

  return { conflict: false };
}
